import { useState, useEffect, useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { resetOnboardingState, setOnboarding, setTemp } from 'modules/state-mgmt/onboarding';
import useLoadAllData from 'hooks/useLoadAllData';
import { getWorkersByProject, cleanupOldWorkers } from 'utils/indexedDB';
import useRestoreAdminContext from 'hooks/useRestoreAdminContext';
import { prettifyErrorResponse } from 'utils/generalUtils';
import { getFirstIncompleteStep } from 'modules/views/shared/Wizard/utils';
import * as uploadManager from 'utils/uploadManager';

import { ROUTES } from 'constants/routes';
import { onboardingStatusToTabMap } from '../types';
import { IOnboardingState } from 'modules/state-mgmt/onboarding/state';
import { OnboardingStatus } from 'models/general';
import { getFieldConfig } from 'utils/fieldConfig';
import { updateProfileFieldConstraintsMap } from 'modules/state-mgmt/cache';

const initialWorkerState = {
  [onboardingStatusToTabMap[OnboardingStatus.PENDING]]: [] as IOnboardingState[],
  [onboardingStatusToTabMap[OnboardingStatus.PROCESSING]]: [] as IOnboardingState[],
  [onboardingStatusToTabMap[OnboardingStatus.INCOMPLETE]]: [] as IOnboardingState[],
  [onboardingStatusToTabMap[OnboardingStatus.COMPLETED]]: [] as IOnboardingState[],
};

const useWorkerList = () => {
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(OnboardingStatus.PENDING);
  const [page, setPage] = useState(1);
  const [workerToDelete, setWorkerToDelete] = useState<IOnboardingState | null>(null);
  const itemsPerPage = 9;
  const {
    onboarding,
    profileFieldConstraintsMap,
    canCreateUnverifiedWorkers,
    consentAgreements,
    project,
    isOffline,
    projectProfileCustomFields,
    workerProfileFieldsForAllCompanies,
    currentCompanyId,
    isFcaUser,
    companies,
    languageCode,
  } = useAppSelector(state => ({
    onboarding: state.onboarding,
    profileFieldConstraintsMap: state.cache.profileFieldConstraintsMap,
    canCreateUnverifiedWorkers: state.cache.canCreateUnverifiedWorkers,
    consentAgreements: state.cache.consentAgreements,
    project: state.cache?.project,
    isOffline: !state.app.isOnline,
    projectProfileCustomFields: state.cache.projectProfileCustomFields,
    workerProfileFieldsForAllCompanies: state.cache.workerProfileFieldsForAllCompanies,
    currentCompanyId: state.user.currentCompanyId,
    isFcaUser: state.user.isFcaUser,
    companies: state.cache.companies,
    languageCode: state.app?.languageCode || 'en',
  }));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [{ hasLoaded, isLoading: isLoadingData, errors }, loadAllData] = useLoadAllData();
  const { restoreIfNeeded } = useRestoreAdminContext();

  const moveUserToFirstIncompleteStep = ({ worker }: { worker?: IOnboardingState } = {}) => {
    if (worker?.uploadingDetails?.errors?.pageUrl) {
      navigate(worker.uploadingDetails.errors.pageUrl);
    } else if (worker?.uploadingDetails?.status === OnboardingStatus.COMPLETED) {
      navigate(ROUTES.PERSONAL_INFORMATION.path);
    } else {
      const step = getFirstIncompleteStep({
        onboarding: worker || onboarding,
        profileFieldConstraintsMap,
        canCreateUnverifiedWorkers,
        consentAgreements,
      });
      if (step) {
        navigate(step.route);
      } else {
        navigate(ROUTES.PERSONAL_INFORMATION.path);
      }
    }
  };

  useEffect(() => {
    if (hasLoaded) {
      moveUserToFirstIncompleteStep();
    }
    if (!isLoadingData && !hasLoaded && errors.length > 0) {
      errors.forEach(error => enqueueSnackbar(prettifyErrorResponse(error, true), { variant: 'error' }));
    }
  }, [hasLoaded, isLoadingData, errors, onboarding]);

  // Determine if current user is a Subcontractor (company role === 2 and not an FCA user)
  const isSubcontractor = useMemo(() => {
    if (isFcaUser) return false;
    const companyList = companies?.[languageCode] || companies?.en || [];
    const userCompany = companyList.find((c: any) => c.id === currentCompanyId);
    if (!currentCompanyId || !companyList?.length || !userCompany) return true; // Default to true if we don't have the necessary data to determine the role
    return userCompany?.role === 2;
  }, [isFcaUser, currentCompanyId, companies, languageCode]);

  // The 'useLiveQuery' hook watches the DB.
  // Any write to the 'workers' table triggers a re-run of this function.
  const allWorkers = useLiveQuery(async () => {
    if (!project?.id) return initialWorkerState;

    const allWorkersByProject = await getWorkersByProject(project.id);

    // Subcontractors can only see workers uploaded by their company
    const filteredWorkers = isSubcontractor
      ? allWorkersByProject.filter(worker => worker.db?.createdByCompanyId === currentCompanyId)
      : allWorkersByProject;

    filteredWorkers.sort((a, b) => {
      const dateA = new Date(a.lastUpdatedAt || 0).getTime();
      const dateB = new Date(b.lastUpdatedAt || 0).getTime();
      return dateB - dateA; // Descending order
    });

    const grouped = filteredWorkers.reduce(
      (acc, worker) => {
        const status = worker.uploadingDetails?.status;

        switch (status) {
          case OnboardingStatus.PENDING:
            acc[onboardingStatusToTabMap[OnboardingStatus.PENDING]].push(worker);
            break;
          case OnboardingStatus.PROCESSING:
            acc[onboardingStatusToTabMap[OnboardingStatus.PROCESSING]].push(worker);
            break;
          case OnboardingStatus.INCOMPLETE:
            acc[onboardingStatusToTabMap[OnboardingStatus.INCOMPLETE]].push(worker);
            break;
          case OnboardingStatus.COMPLETED:
            acc[onboardingStatusToTabMap[OnboardingStatus.COMPLETED]].push(worker);
            break;
          default:
            break;
        }

        return acc;
      },
      JSON.parse(JSON.stringify(initialWorkerState)),
    );

    return grouped;
  }, [project?.id, isSubcontractor, currentCompanyId]); // Dependency Array: Re-run if projectId, role, or company changes

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  useEffect(() => {
    dispatch(resetOnboardingState({ resetTemp: true }));
    // Restore admin project context if a worker login overwrote it
    restoreIfNeeded();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup old workers from IndexedDB
  useEffect(() => {
    if (project?.id) {
      cleanupOldWorkers(project.id).catch(err => {});
    }
  }, [project?.id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setError(null);
  };

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const onEditWorker = async (worker: IOnboardingState) => {
    try {
      // `resumeProjectOnboarding` is a one-shot login hand-off (CH-4652) that gets persisted to
      // IndexedDB along with the rest of `temp` (`fetchWorkerDetails.fulfilled` is a SAVE_TRIGGER,
      // so a tab closed mid-resume leaves it behind). Drop it on restore — on BOTH branches — so an
      // abandoned flow cannot revive itself days later when an admin reopens this worker.
      const { resumeProjectOnboarding, ...restoredTemp } = worker.temp || {};
      const restoredWorker = { ...worker, temp: restoredTemp };

      if (restoredWorker.uploadingDetails?.isOffline || isOffline) {
        await dispatch(setOnboarding(restoredWorker));

        // Setting up calculated profile field constraints map for selected companies
        if (projectProfileCustomFields?.length > 0 && workerProfileFieldsForAllCompanies?.length > 0) {
          const workerProfileCustomFields = getFieldConfig(
            workerProfileFieldsForAllCompanies || [],
            projectProfileCustomFields || [],
            worker.temp?.employers?.map((e: any) => e.value),
          );
          dispatch(updateProfileFieldConstraintsMap(workerProfileCustomFields));
        }

        moveUserToFirstIncompleteStep({ worker: restoredWorker });
        return;
      } else {
        dispatch(setTemp(restoredTemp));
      }
      loadAllData({
        workerId: worker.id,
        employers: worker.temp?.employers?.map(c => c.value),
        projectId: worker.temp?.project?.value,
        onlyImportantApis: true,
      });
    } catch (err) {
      enqueueSnackbar(prettifyErrorResponse(err), { variant: 'error' });
    }
  };

  const workerList = useMemo(() => {
    const getWorkersByPage = (status: OnboardingStatus) =>
      allWorkers?.[onboardingStatusToTabMap[status]]?.slice((page - 1) * itemsPerPage, page * itemsPerPage) || [];

    switch (activeTab) {
      case OnboardingStatus.PENDING:
        return getWorkersByPage(OnboardingStatus.PENDING);
      case OnboardingStatus.PROCESSING:
        return getWorkersByPage(OnboardingStatus.PROCESSING);
      case OnboardingStatus.INCOMPLETE:
        return getWorkersByPage(OnboardingStatus.INCOMPLETE);
      case OnboardingStatus.COMPLETED:
        return getWorkersByPage(OnboardingStatus.COMPLETED);
      default:
        return [];
    }
  }, [activeTab, allWorkers, page, itemsPerPage]);

  const workerCounts = useMemo(() => {
    const pendingCount = allWorkers?.[onboardingStatusToTabMap[OnboardingStatus.PENDING]]?.length;
    const processing = allWorkers?.[onboardingStatusToTabMap[OnboardingStatus.PROCESSING]]?.length;
    const incompleteCount = allWorkers?.[onboardingStatusToTabMap[OnboardingStatus.INCOMPLETE]]?.length;
    const completedCount = allWorkers?.[onboardingStatusToTabMap[OnboardingStatus.COMPLETED]]?.length;

    return {
      pending: pendingCount,
      processing: processing,
      incomplete: incompleteCount,
      completed: completedCount,
    };
  }, [allWorkers]);

  const totalResults = useMemo(() => {
    if (activeTab === OnboardingStatus.PENDING) {
      return allWorkers?.[onboardingStatusToTabMap[OnboardingStatus.PENDING]]?.length || 0;
    } else if (activeTab === OnboardingStatus.PROCESSING) {
      return allWorkers?.[onboardingStatusToTabMap[OnboardingStatus.PROCESSING]]?.length || 0;
    } else if (activeTab === OnboardingStatus.INCOMPLETE) {
      return allWorkers?.[onboardingStatusToTabMap[OnboardingStatus.INCOMPLETE]]?.length || 0;
    } else {
      return allWorkers?.[onboardingStatusToTabMap[OnboardingStatus.COMPLETED]]?.length || 0;
    }
  }, [activeTab]);

  const paginationInfo = {
    totalResults: totalResults,
    totalPages: Math.ceil(totalResults / itemsPerPage),
    pageSize: itemsPerPage,
    currentPage: page,
  };

  // Manual upload handler for "Upload Data" button
  const onUploadWorker = (worker: IOnboardingState) => {
    if (worker.db?.id && !isOffline) {
      uploadManager.uploadManual(worker.db.id);
    }
  };

  return {
    workerList,
    workerCounts,
    isLoading: isLoadingData,
    error,
    activeTab,
    page,
    paginationInfo,
    workerToDelete,
    handleTabChange,
    handlePageChange,
    setWorkerToDelete,
    onEditWorker,
    onUploadWorker,
  };
};

export default useWorkerList;
